/**
 * SafetyStatusPill — live posture badge for the public Landing page.
 *
 * Fetches /api/public/safety-posture (unauthenticated) and renders:
 *   • Green pill when live_mode_enabled=false (shadow/paper).
 *   • Amber+alert when live_mode_enabled=true (real orders).
 *
 * Polls every 30 s. Each successful re-confirmation pulses the
 * verified-dot briefly — a visible "re-verified" trust signal.
 *
 * Read-only diagnostic. No mutation. No enforcement.
 */
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { ShieldCheck, AlertTriangle } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const MONO = 'JetBrains Mono, ui-monospace, monospace';
const C_GREEN = '#16a34a';
const C_AMBER = '#f59e0b';
const C_RED = '#dc2626';
const POLL_MS = 30_000;

// Injected once — a keyframes rule so the dot can briefly pulse each
// time we re-verify the safety posture.
const PULSE_STYLE_ID = 'safety-pill-pulse-keyframes';
function ensurePulseStyle() {
  if (typeof document === 'undefined') return;
  if (document.getElementById(PULSE_STYLE_ID)) return;
  const el = document.createElement('style');
  el.id = PULSE_STYLE_ID;
  el.innerHTML = `
    @keyframes apex-safety-pulse {
      0%   { transform: scale(1);   opacity: 1;   }
      45%  { transform: scale(2.2); opacity: 0.5; }
      100% { transform: scale(1);   opacity: 1;   }
    }
  `;
  document.head.appendChild(el);
}

export default function SafetyStatusPill() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [pulseKey, setPulseKey] = useState(0);
  const [verifyCount, setVerifyCount] = useState(0);
  const mounted = useRef(true);

  useEffect(() => {
    ensurePulseStyle();
    mounted.current = true;
    let timer = null;

    const fetchPosture = async () => {
      try {
        const r = await axios.get(
          `${BACKEND}/api/public/safety-posture`,
          { timeout: 5000 });
        if (!mounted.current) return;
        setData(r.data);
        setErr(null);
        // Bump the animation key so the pulse restarts even when
        // React would otherwise skip re-render (same-payload case).
        setPulseKey(k => k + 1);
        setVerifyCount(c => c + 1);
      } catch (e) {
        if (!mounted.current) return;
        setErr(e?.response?.data?.detail || e.message);
      }
    };

    fetchPosture();
    timer = setInterval(fetchPosture, POLL_MS);
    return () => {
      mounted.current = false;
      if (timer) clearInterval(timer);
    };
  }, []);

  const live = Boolean(data?.live_mode_enabled);
  const color = err ? C_RED : (live ? C_AMBER : C_GREEN);
  const Icon = live ? AlertTriangle : ShieldCheck;
  const label = err
    ? 'STATUS · UNVERIFIED'
    : (data ? `${data.banner}` : 'CHECKING STATUS…');

  return (
    <div
      data-testid="safety-status-pill"
      data-live={live ? 'true' : 'false'}
      data-error={err ? 'true' : 'false'}
      data-verify-count={verifyCount}
      title={
        err
          ? `Could not verify posture: ${err}`
          : (data
              ? `Verified ${new Date(data.verified_at).toLocaleString()} — `
                + `live_mode=${data.live_mode_enabled}, `
                + `effective_mode=${data.effective_mode}, `
                + `signal_quality_layer_enforced=${data.signal_quality_layer_enforced}. `
                + `Re-verifying every ${POLL_MS / 1000}s.`
              : 'Loading posture…')
      }
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 8,
        padding: '8px 14px', borderRadius: 8,
        background: 'rgba(0, 0, 0, 0.35)',
        border: `1px solid ${color}`,
        color, fontFamily: MONO, fontSize: 11,
        fontWeight: 700, letterSpacing: 1.2,
        textTransform: 'uppercase',
        marginTop: 20,
      }}>
      <Icon size={14} />
      {label}
      {data && !err && (
        <span
          key={`pulse-${pulseKey}`}
          data-testid="safety-status-pill-verified-dot"
          style={{
            marginLeft: 4,
            width: 6, height: 6, borderRadius: '50%',
            background: color,
            boxShadow: `0 0 8px ${color}`,
            animation: 'apex-safety-pulse 900ms ease-out 1',
            transformOrigin: 'center',
          }} />
      )}
    </div>
  );
}
